// Maps a /api/bootstrap payload (D1 rows) into the design Component's in-memory
// state shape. The UI is unchanged — only the data source becomes D1.
// Design state shapes reproduced here:
//   majors:      { 'm-d': [title, prepDays, aspect] }        (m is 0-based)
//   tasksByDay:  { 'm-d': [{ t, tm, d, p, a, cat, loc, who, rem, done }] }
//   kanban:      { Inbox:[{id,t,p}], Active:[...], Someday:[...] }
//   habitNames:  [name]
//   habitOv:     { 'm-d': { habitIndex: bool } }   (authoritative for past days)

const partsFromISO = (iso) => { const [, mo, da] = iso.split('-').map(Number); return [mo - 1, da]; };
const pad2 = (n) => String(n).padStart(2, '0');
const isoOf = (year, m, d) => `${year}-${pad2(m + 1)}-${pad2(d)}`;

export function mapD1ToState(data, C) {
  const year = data.year || 2026;
  const majors = {};
  const tasksByDay = {};
  const push = (k, item) => { (tasksByDay[k] || (tasksByDay[k] = [])).push(item); };

  for (const e of data.events || []) {
    const [m, d] = partsFromISO(e.event_date);
    const k = m + '-' + d;
    if (e.is_major) {
      // Array carries an _eid prop so write-back can update/delete the row.
      majors[k] = Object.assign([e.title, e.prep_days || 0, e.aspect || 'work'], { _eid: e.id });
      continue;
    }
    const allDay = !!e.all_day;
    const item = {
      t: e.title, tm: allDay ? '—' : (e.start_time || '—'), d: e.duration_min || 60, p: e.priority || 0,
      a: e.aspect || 'work', cat: e.category || 'Meeting',
      loc: e.location || '', who: e.attendees || '', rem: !!e.reminder, done: !!e.done,
      _eid: e.id, allDay, endDate: e.end_date || null,
      gcal: e.google_calendar_id || 'primary', notes: e.notes || '',
    };
    if (e.end_date && e.end_date > e.event_date) {
      // Multi-day: place an untimed copy on each covered day, all sharing _eid.
      const [em, ed] = partsFromISO(e.end_date);
      const startDoy = C.doy(m, d), endDoy = Math.min(C.doy(em, ed), C.YDAYS || 365), total = endDoy - startDoy + 1;
      for (let dy = startDoy; dy <= endDoy; dy++) {
        const [pm, pd] = C.fromDoy(dy);
        push(pm + '-' + pd, { ...item, tm: '—', days: total, spanNth: dy - startDoy + 1, spanTotal: total });
      }
    } else {
      push(k, item);
    }
  }
  for (const t of data.tasks || []) {
    if (!t.scheduled_date) continue;
    const [m, d] = partsFromISO(t.scheduled_date);
    push(m + '-' + d, {
      t: t.title, tm: t.start_time || '—', d: t.duration_min || 60, p: t.priority || 0,
      a: t.aspect || 'work', done: !!t.done, _tid: t.id,
    });
  }

  // Backlog (kanban) = unscheduled tasks, bucketed by status.
  const kanban = { Inbox: [], Active: [], Someday: [] };
  const colOf = { inbox: 'Inbox', active: 'Active', someday: 'Someday' };
  for (const t of data.tasks || []) {
    if (t.scheduled_date) continue;
    const col = colOf[t.status];
    if (col) kanban[col].push({ id: 'd' + t.id, t: t.title, p: t.priority || 0 });
  }

  // Habits + logs. Fill habitOv for every past day so D1 is authoritative
  // (otherwise the design falls back to its deterministic hash for un-logged days).
  const habits = data.habits || [];
  const habitNames = habits.map((h) => h.name);
  const habitIds = habits.map((h) => h.id);
  const logSet = new Set((data.habitLogs || []).filter((l) => l.done).map((l) => l.habit_id + '|' + l.log_date));
  const habitOv = {};
  const todayDoy = C.todayDoy ? C.todayDoy() : C.doy(6, 20); // real today
  for (let m = 0; m < 12; m++) {
    for (let d = 1; d <= C.DIM[m]; d++) {
      if (C.CUM[m] + d > todayDoy) break;
      const ov = {};
      habitIds.forEach((id, hi) => { ov[hi] = logSet.has(id + '|' + isoOf(year, m, d)); });
      habitOv[m + '-' + d] = ov;
    }
  }

  const out = { majors, tasksByDay, kanban, habitOv };
  if (habitNames.length) out.habitNames = habitNames;
  return out;
}
